import { Meter } from '../../advanced-settings/powermeter'
import { ControlledDto, ControllerDto, DeviceStatus, MyPvControllerDto } from '../../installation/api'
import { ModbusDeviceReference } from '../../../wallboxes/models/wallbox-reference'

type NumberInput = number | ''

export interface InstallationFormModel {
  /**
   * installationId
   *
   * If null, the installation is new.
   */
  id: number | null

  /** installation.projectName */
  name: string

  /** Device data about the EMC device.  */
  emcDevice: Emc | null
  /** Device data about the storage device.  */
  storageDevices: Storage[]
  /**
   * installation.gpsLatitude
   *
   * Stored separately to not affect the dirty state of the general settings tab.
   * It is saved when general settings are saved.
   */
  gpsLatitude: string
  /**
   * installation.gpsLongitude
   *
   * Stored separately to not affect the dirty state of the general settings tab.
   * It is saved when general settings are saved.
   */
  gpsLongitude: string

  /* Tabs */

  generalSettings: GeneralSettings
  /** Data specificly about the Storage tab. It includes data from device config endpoints. */
  storageSystem: StorageSystem
  chargingStations: {
    modbusTcp: ModbusTcpChargingStation[]
    eebus: EebusChargingStation[]
  }

  /**
   * Expanded state of each charging station based on its id.
   * This is kept separate of charging stations so it can be easily removed from the form's
   * dirty state and also not affect each charging station's dirty state.
   *
   * modbusTcp and eebus states are separate to compute "all expanded" and "all collapsed" states for each group separately.
   */
  chargingStationsExpandedState: {
    modbusTcp: { [id: string]: boolean }
    eebus: { [id: string]: boolean }
  }

  heatpump: HeatpumpIo | null
  meters: Meter[]
  ioInterfaces: IoInterfaces
}

export interface IoInterfaces {
  htc320h: Htc320hIo | null
  storage: StorageIo | null
  mypv: MyPvIo | null
}

export type DeviceCategory = 'EMC' | 'Storage' | 'ModbusTCP Evse' | 'EEBus Evse' | 'Heatpump' | 'Meter' | 'HTC320H I/O' | 'Storage I/O' | 'MyPV I/O'

export interface Device {
  /** Front End only id to separate the devices */
  id: string

  /**
   * Front End value to distinguise devices that where newly created from ones that came from the Server.
   *
   * - If true, this device is handled in a POST request.
   * - If false, this device is handled in a PUT request.
   */
  isNew: boolean

  /** Id of devices from the server. It's empty for new devices. */
  deviceId: string

  /**
   * Used to navigate to the device from the router (or clicking the eye icons).
   *
   * For saved devices, it is the same as deviceId. For unsaved devices, it is a random uuid.
   */
  navigationId: string

  deviceCategory: DeviceCategory

  /**
   * - "EMC" = Always "EMC"
   * - "Storage" = deviceLink.deviceName
   * - "Evse":
   *   - "ModbusTcp" = evse.deviceName
   *   - "EEbus" = evse.deviceName
   * - "Meter" = meter.deviceName
   * - "IO" = io.deviceName
   */
  name: string

  /**
   * - "EMC" = deviceLink.deviceShortUuid
   * - "Storage" = deviceLink.deviceShortUuid || deviceLink.deviceId
   * - "Evse":
   *   - "ModbusTcp" = evse.mediaParameters.macAddress
   *   - "EEbus" = evse.mediaParameters.ski
   * - "Meter" = meter.mediaParameters.address
   * - "IO", io.type, io.media:
   *   - "IOController", "ModbusRTU" = io.mediaParameters.address
   *   - "IOController", "Storage" = io.mediaParameters.deviceShortUuid
   *   - "MyPvHeater", "ModbusTCP" = io.mediaParameters.serialNumber
   *   - "HpWithISG", "ModbusTCP" = io.mediaParameters.macAddress
   */
  pairingId: string
  isPaired: boolean

  /**
   * - "EMC" = deviceLink.deviceId (prepended with "FR2-")
   * - "Storage" = deviceLink.deviceId (prepended with "XCM" if deviceId starts with "33" or "XEM" otherwise)
   * - "Evse":
   *   - "ModbusTcp" = evse.mediaParameters.macAddress
   *   - "EEbus" = evse.evseSubTypeParameters.wallboxId
   * - "Meter" = meter.mediaParameters.address
   * - "IO", io.type, io.media:
   *   - "IOController", "ModbusRTU" = = io.mediaParameters.address
   *   - "IOController", "Storage" = = io.mediaParameters.deviceLinkId (same prepend calculation as storage)
   *   - "MyPvHeater", "ModbusTCP" = = io.mediaParameters.serialNumber
   *   - "HpWithISG", "ModbusTCP" = = io.mediaParameters.macAddress
   */
  serialNumber: string

  deviceStatus: DeviceStatus
  deviceStatusCodeBits: number | null
}

export interface Emc extends Device {
  /** emc.deviceId */
  emcHardwareDeviceId: string | null

  /**
   * Server pairing id,
   * deviceLink.deviceShortUuid
   *
   * Read-only value of the pairing id on the server to determine if the pairing id
   * was changed and the /replace endpoint must be called. It starts out the same
   * as pairingId.
   */
  initialPairingId: string
}
export interface Storage extends Device {
  /** storage.deviceId */
  storageHardwareDeviceId: string | null

  /**
   * storage.deviceId
   * - if deviceId starts with "XEM-31" or "31", "XEM - R1"
   * - if deviceId starts with "33", "XCM"
   * - null otherwise
   */
  deviceType: 'XEM - R1' | 'XCM - R2' | null

  /**
   * Server pairing id,
   * deviceLink.deviceShortUuid || deviceLink.deviceId
   *
   * Read-only value of the pairing id on the server to determine if the pairing id
   * was changed and the /replace endpoint must be called. It starts out the same
   * as pairingId.
   */
  initialPairingId: string
}

export interface GeneralSettings {
  location: {
    /** installation.streetName */
    street: string
    /** installation.streetNumber */
    houseNumber: string
    /** installation.zip */
    postalCode: string
    /** installation.city */
    location: string
    /** installation.countryCode */
    country: string
    /** installation.timezone */
    timeZone: string
  }

  gridSettings: {
    /**
     * installationConfig.phaseCount
     * - "single-phase" = 1
     * - "three-phases" = 3
     */
    numberOfPhases: 'single-phase' | 'three-phases'
    /** installationConfig.mainFuse (number) */
    mainProtection: NumberInput
    /** installationConfig.mainFuseDerated (number) */
    thermalDerating: NumberInput

    gridMeter: {
      /** emcConfig.useCT */
      hasElectricalCurrentTransformer: boolean
      /** emcConfig.ratioCT */
      electricalCurrentTransformerRatio: string
    }
  }
}

export interface WeekDays {
  /** emcIdlePeriods[index].days.mo */
  monday: boolean
  /** emcIdlePeriods[index].days.tu */
  tuesday: boolean
  /** emcIdlePeriods[index].days.we */
  wednesday: boolean
  /** emcIdlePeriods[index].days.th */
  thursday: boolean
  /** emcIdlePeriods[index].days.fr */
  friday: boolean
  /** emcIdlePeriods[index].days.sa */
  saturday: boolean
  /** emcIdlePeriods[index].days.su */
  sunday: boolean
}

export type WeekDay = keyof WeekDays
export const ALL_DAYS: ReadonlyArray<WeekDay> = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']

/** emcIdlePeriods[index] */
export type BlockingWeeklySchedule = {
  /** emcIdlePeriods[index].from */
  fromTime: Date
  /** emcIdlePeriods[index].to */
  toTime: Date
  weekDays: WeekDays
}

export interface StorageSystem {
  generalSettings: {
    /** installationConfig.rootPowerMeter the id of the deviceLink that is used for the wiring topology */
    wiringTopologyDeviceId: number | null

    systemRegulation: {
      /** installationConfig.peakPowerPv (number) */
      peakPower: NumberInput
      /** installationConfig.eegDerateAtConnectedPower */
      hasFeedInLimit: boolean
      /**
       * installationConfig.eegValue (number)
       *
       * Editable only when hasFeedInLimit is true.
       */
      feedInRatio: NumberInput
      /**
       * installationConfig.eegConnectedPower (number)
       *
       * Editable only when hasFeedInLimit is true
       */
      feedInMaxPower: NumberInput
    }

    chargingConfiguration: {
      /**
       * storageConfig.emsBatteryBeforeCarMode
       * - "battery" = true
       * - "charging station" = false
       */
      priorityChargingDevice: PriorityChargingDevice
      /**
       * installationConfig.emsBatteryToCarMode
       *
       * Used only when priorityChargingDevice is "charging station"
       */
      useBatteryDischargeOnlyWhenPvIsExceeded: boolean

      /**
       * installationConfig.emsWallboxEnforcePowerAssignment
       *
       * Used only when priorityChargingDevice is "charging station"
       */
      useBatteryDischargeOnlyInOtherMode: boolean
    }

    advancedSettings: {
      weatherForecastOptimization: {
        /**
         * installationConfig.emsWeatherRegulatedChargeEnabled
         *
         * When turned OFF, the weatherOptimizationOption are hidden.
         * @default true
         */
        useWeatherForecastBasedCharging: boolean

        /**
         * installationConfig.sgrCooldown
         *
         * - false: "Storage batteries"
         * - true: "Heat pump via SG-Ready"
         * @default "Heat pump via SG-Ready"
         */
        weatherOptimizationOption: WeatherOptimizationOption
      }

      emergencyPower: {
        /** installationConfig.emergencyPowerCheckPartialGridFailure */
        useEmergencyPower: boolean
        /** installationConfig.emergencyPowerDelayInSeconds (number) */
        emergencyPowerDelay: NumberInput
      }

      /** emcIdlePeriods (with mode="charge") */
      chargeBlockingWeeklySchedule: BlockingWeeklySchedule[]

      /** emcIdlePeriods (with mode="discharge") */
      dischargeBlockingWeeklySchedule: BlockingWeeklySchedule[]
    }
  }

  storageSpecificSettings: {
    deviceId: string | null

    chargingConfiguration: {
      /**
       * storageConfig.pvInverterStringCount
       * - "single tracker" = 1
       * - "duo tracker" = 2
       */
      trackersBehaviour: TrackersBehaviour
    }

    advancedSettings: {
      // storageBattery.batDcbCount
      batteryNumber: number | null

      batteryOperationRange: {
        isStorageSingleBattery: boolean

        /** storageConfig.emsOperatingRangeAuto */
        isAuto: boolean

        /**
         * storageConfig.emsMaxChargePower (number)
         *
         * Used only when isAuto is false.
         */
        maximumChargingPower: NumberInput

        /**
         * storageConfig.emsMaxDischargePower (number)
         *
         * Used only when isAuto is false.
         */
        maximumDischargingPower: NumberInput

        /**
         * storageConfig.emsDischargeStartPower (number)
         *
         * Used only when isAuto is false.
         */
        minimumConsumptionPowerToStartDischarge: NumberInput
      }

      emergencyPower: {
        /** storageConfig.emergencyPowerCapacityReserve (number) */
        emergencyPowerReserve: NumberInput
      }

      /** storageConfig.emsPowersaveEnabled */
      useStorageSystemEnergySavingMode: boolean
    }
  }[]
}

//#region Export types for better readability

//#region StorageSystem
/**
 * storageConfig.pvInverterStringCount
 * - "single tracker" = 1
 * - "duo tracker" = 2
 */
export type TrackersBehaviour = 'single tracker' | 'duo tracker'
/**
 * installationConfig.emsBatteryBeforeCarMode
 * - "battery" = true
 * - "charging station" = false
 */
export type PriorityChargingDevice = 'charging station' | 'battery'

//#region StorageSystem Extended
/**
 * storageConfig.sgrCooldown
 *
 * - false: "Storage batteries"
 * - true: "Heat pump via SG-Ready"
 * @default "Heat pump via SG-Ready"
 */
export type WeatherOptimizationOption = 'Storage batteries' | 'Heat pump via SG-Ready'
//#endregion

//#endregion

//#endregion

/**
 * - "standard" (Boost mode)
 *   - evse.parameters.sunMode.activated = false
 *   - evse.parameters.sunMode.chargingStrategy = "Disabled"
 * - "pvOnly" (PV surplus)
 *   - evse.parameters.sunMode.activated = true
 *   - evse.parameters.sunMode.chargingStrategy = "Disabled"
 * - "pvImmediate" (Safe PV surplus)
 *   - evse.parameters.sunMode.activated = true
 *   - evse.parameters.sunMode.chargingStrategy = "Immediate"
 * - "pvDelayed" (Efficient PV surplus)
 *   - evse.parameters.sunMode.activated = true
 *   - evse.parameters.sunMode.chargingStrategy = "Delayed"
 */
export const ChargingMode = ['standard', 'pvOnly', 'pvImmediate', 'pvDelayed'] as const
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type ChargingMode = (typeof ChargingMode)[number]

/**
 * evse.parameters.sunMode.parameterList
 *
 * Parsed in an array of 7 elements in the form of
 * {
 *   "daytime": "0",
 *   "minEnergy": "0"
 * }
 * one for each day of the week.
 *
 * Use only if chargingMode is 'pvImmediate' or 'pvDelayed', else use an empty array in PUT and POST requests.
 */
export type ChargingSchedule = {
  weekDays: WeekDays

  /**
   * In DTO, it is a string with the number of seconds since the beginning of the day.
   * In form, it is a ISO date.
   */
  departureTime: string

  /** In kilowatts per hour (kWh) */
  energyWanted: NumberInput
}[]

export interface ModbusTcpChargingStation extends Device {
  /** evse.evseSubTypeParameters.brand */
  brand: 'Hager'
  /**
   * evse.evseSubTypeParameters.wbType
   * - "6" or null (Cintech) = "XEV1K07T2S / XEV1K22T2S"
   * - "8" (WittyLink) = "XVL122C / XVL122S"
   * - "11" (WittyFlex / EDSN) = "XDV111E"
   */
  reference: ModbusDeviceReference

  /**
   * The concrete device reference value, like:
   * XEV1K07T2S, XEV1K22T2S, XVL122C, XVL122S
   */
  deviceReference: string

  /** evse.parameters.protection */
  protection: NumberInput
  /** evse.parameters.fallbackMaxCurrent */
  fallbackMaxCurrent: NumberInput
  /** evse.parameters.phasesManagement */
  phaseManagement: 'AutoPhaseSwitch' | '1PhBlocked' | '3PhBlocked'
  /** evse.parameters.phaseMapping */
  phaseMapping: '123' | '132' | '231' | '213' | '312' | '321'
  /** evse.parameters.minCurrent */
  minCharge: NumberInput
  /**
   * - "no mid meter"
   *   - evse.parameters.useMidMeter = false
   *   - evse.parameters.pulseWeight = 0
   * - "ECP140D"
   *   - evse.parameters.useMidMeter = true
   *   - evse.parameters.pulseWeight = 1
   * - "ECP380D"
   *   - evse.parameters.useMidMeter = true
   *   - evse.parameters.pulseWeight = 5
   */
  midMeter: 'no mid meter' | 'ECP140D' | 'ECP380D'

  carAtHomeBehavior: {
    /** evse.evseSubTypeParameters.ocppActivation */
    isEnabled: boolean
    /** evse.evseSubTypeParameters.ocppServerAddress */
    chargingPointOperatorWs: string
    /** evse.evseSubTypeParameters.ocppId */
    ocppId: string
    /**
     * evse.evseSubTypeParameters.ocppAuthType
     * - "No" or null = false
     * - "basic" = true
     */
    isCredentialsActive: boolean

    /**
     * Always starts as an empty string.
     *
     * During submit, after tha wallbox is created or updated, and if this field is dirty and not empty, a PUT request is sent to
     * `${INSTALLATIONS_URL}/installations/${installationId}/device-links/${emcDeviceId}/sub/${modbusTcpDeviceId}/ocpp/password`
     * with the BODY { password } to update the password.
     */
    password: string
  }

  /** evse.parameters.lockCable */
  isCableLocked: boolean

  /** evse.parameters.ledIntensity */
  ledIntensityPercentage: NumberInput

  /**
   * evse.parameters.authenticationMode
   *
   * Readonly value. It is used to display a warning if the value is changed specifically
   * from "freeAccess" to another value.
   */
  initialAccessManagementOptions: 'freeAccess' | 'key' | 'badge'

  /** evse.parameters.authenticationMode */
  accessManagementOptions: 'freeAccess' | 'key' | 'badge'
  chargingMode: ChargingMode
  /** evse.parameters.sunMode.holdingTimeInMin */
  holdingTime: NumberInput
  /**
   * evse.parameters.minCurrentSunMode
   *
   * Show only if chargingMode is NOT "standard".
   */
  solarRatio: NumberInput
  safePvSchedule: ChargingSchedule
  efficientPvSchedule: ChargingSchedule

  /**
   * evse.mediaParameters.ski
   * Read-only, initial pairing id from the server.
   */
  initialPairingId: string
  /** Initial data from the server. */
  rawData: Nullable<ControlledDto> | null

  /**
   * When true, MAC origin was verified via network discovery - skip manufacturing validation.
   * Used when device is selected from network scan.
   */
  macOriginVerified?: boolean
}

export interface EebusChargingStation extends Device {
  /** evse.evseSubTypeParameters.brand */
  brand: 'Hager' | 'Audi'
  /**
   * evse.evseSubTypeParameters.brand
   * - "Hager" | null = "XEV1S22T2TF"
   * - "Audi" = "8V4, 9Y0"
   */
  reference: 'XEV1S22T2TF' | '8V4, 9Y0'

  /** evse.parameters.protection */
  protection: NumberInput
  /** evse.parameters.minCurrent */
  minCharge: NumberInput

  chargingMode: ChargingMode
  /** evse.parameters.sunMode.holdingTimeInMin */
  holdingTime: NumberInput
  /**
   * evse.parameters.minCurrentSunMode
   *
   * Show only if chargingMode is NOT "standard".
   */
  solarRatio: NumberInput
  safePvSchedule: ChargingSchedule
  efficientPvSchedule: ChargingSchedule

  /**
   * evse.mediaParameters.macAddress
   * Read-only, initial pairing id from the server.
   */
  initialPairingId: string

  /** Initial data from the server. */
  rawData: Nullable<ControlledDto> | null
}

export interface Htc320hIo extends Device {
  /** io.outputs.[number].typeParameters.nominalPower */
  nominalPower: NumberInput

  /** io.inputs.[0].active */
  isConsumptionActive: boolean

  /** io.inputs.[0].value */
  totalPowerConsumption: NumberInput

  /** io.inputs.[1].active */
  isProductionActive: boolean

  /** Initial data from the server. */
  rawData: Nullable<ControllerDto> | null
}

export interface StorageIo extends Device {
  /** io.outputs.[number].typeParameters.nominalPower */
  nominalPower: NumberInput
  /** The link ID of the emc device. */
  deviceLinkId: number
  /** The link ID of the storage device. */
  storageDeviceLinkId: number
}
export type BoostConfig = {
  /** Front-End only id to uniquely identify the schedules. */
  id: string

  securityTemperature: NumberInput

  fromTime: Date
  toTime: Date
  weekDays: WeekDays
}[]
export type OptimizationMode = MyPvIo['optimizationMode']
export interface MyPvIo extends Device {
  /**
   * io.parameters.controlMode
   *
   * "Battery" and "Derated" are currently unused.
   */
  optimizationMode: 'All' | 'Solar' | 'Surplus' | 'No' | 'Battery' | 'Derated'

  /** io.parameters.boostConfig */
  boostConfig: BoostConfig

  /** Initial data from the server. */
  rawData: Nullable<MyPvControllerDto> | null
}

export interface HeatpumpIo extends Device {
  /**
   * io.parameters.nominalPower
   *
   * - min 1
   * - max 23000
   */
  nominalPower: NumberInput

  /**
   * io.mediaParameters.macAddress
   * Read-only, initial pairing id from the server.
   */
  initialPairingId: string
}

export type IoInterfaceType = keyof IoInterfaces
